/**
 * Hand-written field spec mirroring DroneSchema's groups, used to generate the "Open the engine" form.
 */
export type FieldKind = 'number' | 'int' | 'bool' | 'enum';
export interface FieldSpec { path: string; label: string; kind: FieldKind; min?: number; max?: number; step?: number; options?: readonly string[]; unit?: string }
export interface GroupSpec { key: string; label: string; hint?: string; fields: FieldSpec[] }

const n = (path: string, label: string, min: number, max: number, step = 0.01, unit?: string): FieldSpec => ({ path, label, kind: 'number', min, max, step, unit });
const i = (path: string, label: string, min: number, max: number): FieldSpec => ({ path, label, kind: 'int', min, max, step: 1 });
const b = (path: string, label: string): FieldSpec => ({ path, label, kind: 'bool' });
const e = (path: string, label: string, options: readonly string[]): FieldSpec => ({ path, label, kind: 'enum', options });

const LFO = ['sine', 'tri', 'random', 'perlin'] as const;

export const ENGINE_GROUPS: GroupSpec[] = [
  { key: 'layers', label: 'Layers', hint: 'Octave and fifth partners, unison detune.', fields: [
    b('layers.sub.on', 'Sub octave'), n('layers.sub.levelDb', 'Sub level', -60, 12, 0.5, 'dB'),
    b('layers.fifth.on', 'Fifth'), n('layers.fifth.levelDb', 'Fifth level', -60, 12, 0.5, 'dB'),
    b('layers.octaveUp.on', 'Octave up'), n('layers.octaveUp.levelDb', 'Octave up level', -60, 12, 0.5, 'dB'),
    i('layers.unison.voices', 'Unison voices', 1, 7), n('layers.unison.detuneCents', 'Detune', 0, 50, 0.5, '¢'),
    n('layers.unison.spread', 'Stereo spread', 0, 1), n('layers.unison.phaseScatter', 'Phase scatter', 0, 1),
  ] },
  { key: 'filter', label: 'Filter', fields: [
    e('filter.type', 'Type', ['none', 'lp', 'hp', 'bp', 'formant', 'comb']), n('filter.cutoffHz', 'Cutoff', 20, 20000, 1, 'Hz'),
    n('filter.q', 'Q', 0.1, 30, 0.1), n('filter.keyTrack', 'Key track', 0, 1), n('filter.combFeedback', 'Comb feedback', 0, 1),
    n('filter.lfo.rateHz', 'LFO rate', 0, 20, 0.01, 'Hz'), n('filter.lfo.depth', 'LFO depth', 0, 1), e('filter.lfo.shape', 'LFO shape', LFO),
  ] },
  { key: 'breath', label: 'Breath', hint: 'Slow amplitude swell.', fields: [
    n('breath.rateHz', 'Rate', 0, 20, 0.01, 'Hz'), n('breath.depth', 'Depth', 0, 1), e('breath.shape', 'Shape', LFO), b('breath.phasePerVoice', 'Random phase per voice'),
  ] },
  { key: 'motion', label: 'Motion', hint: 'Drift keeps a held note alive.', fields: [
    n('motion.driftAmount', 'Drift amount', 0, 1), n('motion.driftRateHz', 'Drift rate', 0, 5, 0.01, 'Hz'), b('motion.idleEvolve', 'Evolve when idle'),
  ] },
  { key: 'space', label: 'Space', fields: [
    n('space.reverb.size', 'Reverb size', 0, 1), n('space.reverb.decaySec', 'Reverb decay', 0.1, 20, 0.1, 's'), n('space.reverb.damping', 'Reverb damping', 0, 1),
    n('space.reverb.preDelayMs', 'Pre-delay', 0, 200, 1, 'ms'), n('space.reverb.mix', 'Reverb mix', 0, 1),
    n('space.delay.timeSec', 'Delay time', 0.01, 2, 0.01, 's'), n('space.delay.feedback', 'Delay feedback', 0, 1), n('space.delay.toneHz', 'Delay tone', 200, 16000, 10, 'Hz'),
    n('space.delay.mix', 'Delay mix', 0, 1), b('space.delay.pingPong', 'Ping-pong'),
    n('space.shimmer.mix', 'Shimmer mix', 0, 1), e('space.shimmer.interval', 'Shimmer interval', ['12', '7']), n('space.shimmer.feedback', 'Shimmer feedback', 0, 1),
    n('space.width', 'Width', 0, 2),
  ] },
  { key: 'envelope', label: 'Envelope', fields: [
    n('envelope.attackSec', 'Attack (swell)', 0, 10, 0.01, 's'), n('envelope.releaseSec', 'Release', 0.05, 30, 0.05, 's'), e('envelope.swellCurve', 'Swell curve', ['lin', 'exp', 'sCurve']),
  ] },
  { key: 'harmony', label: 'Harmony', fields: [
    b('harmony.autoOctave', 'Auto octave (latched)'), b('harmony.autoFifth', 'Auto fifth (anchor)'), b('harmony.ji', 'Just intonation'),
  ] },
  { key: 'gesture', label: 'Gesture', hint: 'What finger speed and ring radius control.', fields: [
    e('gesture.velocityTo', 'Velocity →', ['vibrato', 'buzz', 'density', 'growl', 'none']), e('gesture.radiusTo', 'Radius →', ['brightness', 'jivari', 'grainSize', 'tilt']),
    n('gesture.angleBendCents', 'Angle bend', 0, 100, 1, '¢'),
  ] },
];

export const MACROS = ['brightness', 'motion', 'density', 'space', 'shimmer'] as const;

/** Read a dotted path from an object. */
export function getPath(obj: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((o, k) => (o && typeof o === 'object' ? (o as Record<string, unknown>)[k] : undefined), obj);
}

/** Immutable set of a dotted path. */
export function setPath<T>(obj: T, path: string, value: unknown): T {
  const keys = path.split('.');
  const rec = (o: unknown, i: number): unknown => {
    const k = keys[i]!;
    const base = (o && typeof o === 'object' ? o : {}) as Record<string, unknown>;
    if (i === keys.length - 1) return { ...base, [k]: value };
    return { ...base, [k]: rec(base[k], i + 1) };
  };
  return rec(obj, 0) as T;
}
